import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "../lib/api";
import { Users, Building2, ShieldCheck, TrendingUp, Clock, MapPin, Heart } from "lucide-react";

const Metric = ({ icon: Icon, label, value, sub, colorClass = "text-[#0B2B62]", testid }) => (
  <div className="bg-white border border-slate-200 rounded-xl p-5 flex items-start gap-3">
    <div className={`w-10 h-10 rounded-full bg-slate-50 flex items-center justify-center shrink-0`}>
      <Icon className={`w-5 h-5 ${colorClass}`} strokeWidth={1.6} />
    </div>
    <div className="min-w-0">
      <div className="text-xs uppercase tracking-[0.16em] text-slate-500">{label}</div>
      <div className={`font-display font-bold text-3xl ${colorClass} mt-0.5`} data-testid={testid}>{value}</div>
      {sub && <div className="text-xs text-slate-500 mt-0.5">{sub}</div>}
    </div>
  </div>
);

const Impact = () => {
  const [d, setD] = useState(null);
  const [err, setErr] = useState(false);
  useEffect(() => {
    api.get("/public/impact").then(({ data }) => setD(data)).catch(() => setErr(true));
  }, []);

  if (err) return <div className="max-w-5xl mx-auto px-6 py-16 text-center text-slate-500">Não foi possível carregar os dados de impacto agora.</div>;
  if (!d) return <div className="max-w-5xl mx-auto px-6 py-16 text-center text-slate-500">Carregando indicadores…</div>;

  const maxMonth = Math.max(1, ...d.cases_per_month.map((m) => m.count));

  return (
    <div className="max-w-6xl mx-auto px-4 sm:px-6 py-10 sm:py-16">
      <div className="max-w-3xl mb-10 sm:mb-14">
        <div className="text-xs uppercase tracking-[0.18em] text-slate-500 mb-2">Impacto RAF Direct</div>
        <h1 className="font-display font-bold text-4xl sm:text-5xl lg:text-6xl text-[#0B2B62] tracking-tight leading-tight">
          Do primeiro contato à admissão — <span className="text-red-600">com dados abertos.</span>
        </h1>
        <p className="text-lg text-slate-600 mt-4 leading-relaxed">
          A rede RAF Direct é auditável. Aqui você acompanha em tempo real quantas famílias já atendemos,
          quantas unidades estão ativas e quanto tempo levamos entre o pedido de ajuda e a internação.
        </p>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-10">
        <Metric icon={Heart} label="Famílias atendidas" value={d.families_served} sub={`${d.total_cases} pedido(s) de ajuda no total`} colorClass="text-red-600" testid="impact-families" />
        <Metric icon={TrendingUp} label="Internações confirmadas" value={d.cases_admitted} sub={`${d.cases_active} caso(s) ativo(s) agora`} colorClass="text-green-700" testid="impact-admitted" />
        <Metric icon={Clock} label="Tempo médio até internação" value={d.avg_hours_to_admission != null ? `${d.avg_hours_to_admission}h` : "—"} sub="Do pedido à admissão" colorClass="text-blue-700" testid="impact-avg-hours" />
        <Metric icon={MapPin} label="Estados atendidos" value={d.states_served.length} sub={d.states_served.length > 0 ? d.states_served.join(" · ") : "aguardando primeiro caso"} testid="impact-states" />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-10">
        <Metric icon={Users} label="Consultores ativos" value={d.active_consultors} sub="Voluntários certificados na rede" colorClass="text-indigo-700" testid="impact-consultors" />
        <Metric icon={Building2} label="Unidades parceiras" value={d.active_clinics} sub="Clínicas credenciadas com vaga em tempo real" colorClass="text-amber-700" testid="impact-clinics" />
      </div>

      <div className="bg-white border border-slate-200 rounded-xl p-5 sm:p-6 mb-10" data-testid="impact-chart">
        <div className="mb-4">
          <div className="text-[11px] uppercase tracking-widest text-slate-500">Últimos 6 meses</div>
          <h2 className="font-display font-bold text-2xl text-[#0B2B62]">Pedidos de ajuda recebidos por mês</h2>
        </div>
        <div className="flex items-end gap-3 h-40">
          {d.cases_per_month.map((m) => {
            const h = Math.max(6, Math.round((m.count / maxMonth) * 140));
            return (
              <div key={m.month} className="flex flex-col items-center gap-2 flex-1 min-w-0" title={`${m.month}: ${m.count} caso(s)`}>
                <div className="text-xs font-mono font-semibold text-slate-700">{m.count}</div>
                <div style={{ height: `${h}px` }} className="w-full bg-gradient-to-t from-[#0B2B62] to-[#1e4d9e] rounded-t-md" />
                <div className="text-[10px] text-slate-500">{m.month.slice(5)}/{m.month.slice(2, 4)}</div>
              </div>
            );
          })}
        </div>
      </div>

      <div className="rounded-xl bg-gradient-to-br from-[#0B2B62] to-[#1e4d9e] text-white p-6 sm:p-10 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-6">
        <div className="max-w-2xl">
          <div className="text-[11px] uppercase tracking-widest text-blue-200 mb-2">Auditoria contínua</div>
          <h3 className="font-display font-bold text-2xl sm:text-3xl leading-snug">
            Cada número vem de eventos hash-chained na plataforma.
          </h3>
          <p className="text-sm sm:text-base text-blue-100 mt-2 leading-relaxed">
            Não editamos histórico. Não escondemos casos sem internação. Ranking, tempos e destinos são reconstruíveis
            do audit log <b className="text-white">/api/consultant/audit</b> — verificável por qualquer parceiro autorizado.
          </p>
        </div>
        <div className="flex flex-col sm:flex-row gap-2 shrink-0">
          <Link to="/estatisticas" data-testid="impact-cta-stats" className="inline-flex items-center gap-2 px-4 py-2.5 bg-white text-[#0B2B62] rounded-md font-semibold hover:bg-slate-100">
            Ver estatísticas detalhadas <ShieldCheck className="w-4 h-4" />
          </Link>
          <Link to="/seja-parceiro" data-testid="impact-cta-partner" className="inline-flex items-center gap-2 px-4 py-2.5 border-2 border-white text-white rounded-md font-semibold hover:bg-white/10">
            Ser unidade parceira
          </Link>
        </div>
      </div>

      <p className="text-xs text-slate-400 mt-6 text-center">
        Dados atualizados a cada consulta · fonte: MongoDB da produção · última leitura {new Date(d.as_of).toLocaleString("pt-BR")}
      </p>
    </div>
  );
};

export default Impact;
